import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';

import { DOMAINS, getWebSocketProtocol } from '@/shared-utilities';

import { resolveHttpUri } from './apollo.uri.helpers';

export default function getApolloClient(apiHost: string) {
  const isLocalhost = apiHost.startsWith(DOMAINS.LOCALHOST);

  const httpLink = new HttpLink({
    uri: resolveHttpUri({
      apiHost,
      isServer: typeof window === 'undefined',
      isProductionBuild: process.env.NODE_ENV === 'production',
      internalUrl: process.env.SURVEY_GRAPHQL_URL,
    }),
  });

  let link: ApolloLink = httpLink;
  if (typeof window !== 'undefined') {
    const wsLink = new GraphQLWsLink(
      createClient({
        url: `${getWebSocketProtocol(isLocalhost)}://${apiHost}/survey/graphql`,
        // Detect half-open sockets (iOS suspends WS without closing it) — pings beat BE keepAlive of 10s.
        keepAlive: 10_000,
        retryAttempts: 10,
        // Cap exponential backoff at 30s so a downed BE doesn't spin the client forever.
        retryWait: (retries) =>
          new Promise((resolve) =>
            setTimeout(resolve, Math.min(1000 * 2 ** retries, 30_000))
          ),
        // Don't retry on auth/bad-request close codes (4400-4499) — those aren't transient.
        shouldRetry: (err) => {
          if (
            typeof err === 'object' &&
            err !== null &&
            'code' in err &&
            typeof err.code === 'number' &&
            err.code >= 4400 &&
            err.code < 4500
          ) {
            return false;
          }
          return true;
        },
      })
    );

    link = ApolloLink.split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      wsLink,
      httpLink
    );
  }

  return new ApolloClient({
    cache: new InMemoryCache(),
    link,
    dataMasking: true,

    defaultOptions: {
      query: { fetchPolicy: 'no-cache' },
      watchQuery: { fetchPolicy: 'no-cache' },
    },
  });
}
